import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import ProductCard from '@ajat/design-system/ProductCard';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import './Accessories.css';

/**
 * Shared Accessories page — brand-agnostic layout.
 *
 * Brand wrappers (`ABC-SE/Accessories`, `CLS-DK/Accessories`) supply all
 * localised content + Header/Footer components. Keeps identical UX while
 * allowing separate copy, catalog, and navigation targets per brand.
 */
const Accessories = ({
  brand,
  Header,
  Footer,
  bannerTag,
  bannerTitle,
  bannerImage,
  tabs,
  initialTab = 'alle',
  showTabs = true,
  bulkProducts,
  accessoryProducts,
  bottomProducts,
  fullwidth,
  onBulkClick,
  onAccessoryClick,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', brand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [brand]);

  /* ── Filtered product lists ── */
  const showBulk = activeTab === 'alle' || activeTab === 'overalls';
  const showAccessories = activeTab === 'alle' || activeTab === 'accessories';

  const topAccessories = showAccessories ? accessoryProducts : [];
  const bottomAccessories = showAccessories ? bottomProducts : [];
  const hasMixedGrid = showBulk || topAccessories.length > 0;

  return (
    <div className="accessories">
      <Header />

      <SecondaryBanner
        tag={bannerTag}
        title={bannerTitle}
        backgroundImage={bannerImage}
      />

      <main className="accessories__wrapper">
        {/* ── Sub-menu tabs ── */}
        {showTabs && (
          <nav className="accessories__submenu">
            <div className="accessories__submenu-inner">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`accessories__tab typo-link${activeTab === tab.key ? ' accessories__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
        )}

        {/* ── Mixed grid: Bulk (50%) + Accessories (25%) ──
            desktopColumns is pinned to 4 rather than left on auto: the bulk cards
            take `grid-column: span 2` (Accessories.css) to make the 50%/25% split,
            so a tab yielding exactly 3 cards must not collapse this to a 3-up grid
            — a bulk card would jump from 50% to 67% wide. */}
        {hasMixedGrid && (
          <ProductsGrid
            paddingTop="medium-l"
            paddingBottom={bottomAccessories.length > 0 ? 'medium-s' : 'medium-l'}
            background="var(--color-gray-0)"
            desktopColumns={4}
            rowGap="medium-s"
            columnGap="medium-s"
          >
            {showBulk &&
              bulkProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  image={p.image}
                  imageAlt={p.imageAlt}
                  title={p.title}
                  priceValue={p.price}
                  imageAspectRatio="1:1"
                  onClick={() => onBulkClick(p.slug)}
                  className="accessories__card--bulk"
                />
              ))}
            {topAccessories.map((p) => (
              <ProductCard
                key={p.id}
                image={p.image}
                imageAlt={p.imageAlt}
                title={p.title}
                priceValue={p.price}
                onClick={() => onAccessoryClick(p.slug)}
              />
            ))}
          </ProductsGrid>
        )}

        {/* ── Fullwidth Content Widget ── */}
        {showAccessories && fullwidth && (
          <Fullwidth
            showImage={false}
            subtitle={fullwidth.subtitle}
            mainCopyVariant="h2"
            mainCopy={fullwidth.mainCopy}
            tags={fullwidth.tags}
            showHyperlink={false}
          />
        )}

        {/* ── Bottom Products Grid ── */}
        {bottomAccessories.length > 0 && (
          <ProductsGrid
            background="var(--color-gray-0)"
            paddingTop="medium-m"
            paddingBottom="large-l"
            rowGap="medium-s"
            columnGap="medium-s"
            items={bottomAccessories.map((p) => ({
              id: p.id,
              image: p.image,
              imageAlt: p.imageAlt,
              title: p.title,
              price: p.price,
              onClick: () => onAccessoryClick(p.slug),
            }))}
          />
        )}
      </main>

      <Footer />
    </div>
  );
};

const productShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  image: PropTypes.any.isRequired,
  imageAlt: PropTypes.string,
  title: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
});

Accessories.propTypes = {
  brand: PropTypes.oneOf(['ABC', 'CLS']).isRequired,
  Header: PropTypes.elementType.isRequired,
  Footer: PropTypes.elementType.isRequired,
  bannerTag: PropTypes.string.isRequired,
  bannerTitle: PropTypes.string.isRequired,
  bannerImage: PropTypes.any,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  initialTab: PropTypes.string,
  showTabs: PropTypes.bool,
  bulkProducts: PropTypes.arrayOf(productShape).isRequired,
  accessoryProducts: PropTypes.arrayOf(productShape).isRequired,
  bottomProducts: PropTypes.arrayOf(productShape).isRequired,
  fullwidth: PropTypes.shape({
    subtitle: PropTypes.string,
    mainCopy: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  onBulkClick: PropTypes.func.isRequired,
  onAccessoryClick: PropTypes.func.isRequired,
};

export default Accessories;
